import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download, Loader2, Share2 } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useBrandQrLogo } from '@/hooks/useBrandQrLogo'
import { useToast } from '@/hooks/use-toast'
import {
  canvasToBlob,
  KIT_DIMS,
  KIT_FORMATS,
  KIT_LOOKS,
  kitFileName,
  loadImageDataUrl,
  renderKitCanvas,
  type KitFormat,
  type KitLook,
} from '@/lib/kitRender'
import { cn } from '@/lib/utils'
import type { MarketingKit } from '@/types/marketingProactive'

interface ReviewQuote {
  text: string
  author: string
}

/**
 * The kit's visuals. Not an editor: pick the format, a look and which dish
 * photo leads — the words come from the kit, the photos from the menu.
 */
export function KitVisuals({ kit }: { kit: MarketingKit }) {
  const { toast } = useToast()
  const { logoDataUrl } = useBrandQrLogo()
  const reviews = useMemo<ReviewQuote[]>(() => {
    const raw = (kit.context?.reviews as { text?: string; author?: string }[] | undefined) ?? []
    return raw.filter((r) => r.text).map((r) => ({ text: r.text as string, author: r.author || 'A guest' }))
  }, [kit.context])
  const formats = KIT_FORMATS.filter((f) => f.id !== 'quote' || reviews.length > 0)
  const [format, setFormat] = useState<KitFormat>(reviews.length > 0 ? 'quote' : 'post')
  const [look, setLook] = useState<KitLook>('warm')
  const photos = kit.products.filter((p) => p.image_url)
  const [photoId, setPhotoId] = useState<string | null>(photos[0]?.id ?? null)
  const [reviewIdx, setReviewIdx] = useState(0)
  const [qrSlug, setQrSlug] = useState<string>('')

  const qrQuery = useQuery({
    queryKey: ['marketing', 'qr'],
    queryFn: () => apiClient.listMarketingQR(),
    enabled: format === 'poster',
    staleTime: 5 * 60_000,
  })
  const qrItems = (qrQuery.data?.data?.items ?? []).filter((q) => q.is_active)
  const qrBase = (qrQuery.data?.data?.redirect_base ?? '').replace(/\/$/, '')
  useEffect(() => {
    if (qrSlug || qrItems.length === 0) return
    const menu = qrItems.find((q) => /menu/i.test(q.label) || /menu/i.test(q.target_url))
    setQrSlug((menu ?? qrItems[0]).slug)
  }, [qrItems, qrSlug])

  const photo = photos.find((p) => p.id === photoId) ?? photos[0]
  const { data: photoDataUrl, isFetching: photoLoading } = useQuery({
    queryKey: ['kit-photo', photo?.image_url],
    queryFn: () => loadImageDataUrl(photo?.image_url),
    enabled: Boolean(photo?.image_url),
    staleTime: Infinity,
  })

  const input = useMemo(
    () => ({
      format,
      look,
      restaurant: kit.restaurant,
      logoDataUrl,
      photoDataUrl: photoDataUrl ?? null,
      headline: kit.copy.headline,
      subhead: kit.copy.subhead,
      offerLine: kit.copy.offer_line,
      storyText: kit.copy.story_text,
      when: kit.when,
      dishName: photo?.name,
      quote: reviews[reviewIdx] ?? reviews[0],
      qrUrl: format === 'poster' && qrSlug && qrBase ? `${qrBase}/q/${qrSlug}` : '',
      qrCaption: 'Scan for the menu',
    }),
    [format, look, kit, logoDataUrl, photoDataUrl, photo?.name, reviews, reviewIdx, qrSlug, qrBase],
  )

  const [preview, setPreview] = useState<string | null>(null)
  const [rendering, setRendering] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  useEffect(() => {
    let cancelled = false
    setRendering(true)
    const t = setTimeout(async () => {
      try {
        const c = await renderKitCanvas(input)
        if (cancelled) return
        canvasRef.current = c
        const blob = await canvasToBlob(c, 'image/jpeg')
        if (cancelled) return
        setPreview((prev) => {
          if (prev) URL.revokeObjectURL(prev)
          return URL.createObjectURL(blob)
        })
      } catch {
        /* keep the last preview */
      } finally {
        if (!cancelled) setRendering(false)
      }
    }, 200)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [input])
  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fileName = kitFileName(kit.title, format, format === 'quote' ? reviewIdx : undefined)

  const download = async () => {
    try {
      const c = await renderKitCanvas(input)
      const blob = await canvasToBlob(c)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 2000)
    } catch {
      toast({ title: 'Could not make the image', variant: 'destructive' })
    }
  }

  const canShareFiles = typeof navigator !== 'undefined' && typeof navigator.canShare === 'function'
  const share = async () => {
    try {
      const c = await renderKitCanvas(input)
      const blob = await canvasToBlob(c)
      const file = new File([blob], fileName, { type: 'image/png' })
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: kit.copy.caption })
      } else {
        await download()
      }
    } catch {
      /* share sheet dismissed */
    }
  }

  const dims = KIT_DIMS[format]

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Posts and poster</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-1 rounded-lg bg-muted/60 p-1">
          {formats.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFormat(f.id)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                format === f.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {(Object.keys(KIT_LOOKS) as KitLook[]).map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => setLook(l)}
              className={cn(
                'flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm transition-colors',
                look === l ? 'border-primary bg-primary/5' : 'text-muted-foreground hover:text-foreground',
              )}
              aria-pressed={look === l}
            >
              <span className="flex h-4 w-4 overflow-hidden rounded-full border">
                <span className="h-full w-1/2" style={{ background: KIT_LOOKS[l].bg }} />
                <span className="h-full w-1/2" style={{ background: KIT_LOOKS[l].accent }} />
              </span>
              {KIT_LOOKS[l].label}
            </button>
          ))}
        </div>

        {photos.length > 1 && format !== 'quote' && (
          <div className="flex flex-wrap gap-2">
            {photos.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPhotoId(p.id)}
                className={cn('overflow-hidden rounded-md border-2', photo?.id === p.id ? 'border-primary' : 'border-transparent')}
                title={p.name}
              >
                <img src={p.image_url} alt={p.name} className="h-12 w-12 object-cover" />
              </button>
            ))}
          </div>
        )}

        {format === 'quote' && reviews.length > 1 && (
          <div className="space-y-1.5">
            <Label>Review</Label>
            <Select value={String(reviewIdx)} onValueChange={(v) => setReviewIdx(Number(v))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {reviews.map((r, i) => (
                  <SelectItem key={i} value={String(i)}>
                    {r.author}: “{r.text.slice(0, 50)}
                    {r.text.length > 50 ? '…' : ''}”
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {format === 'poster' && (
          <div className="space-y-1.5">
            <Label>QR on the poster</Label>
            {qrItems.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No QR codes yet — make one in Print &amp; QR → QR codes and it appears here.
              </p>
            ) : (
              <Select value={qrSlug} onValueChange={setQrSlug}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {qrItems.map((q) => (
                    <SelectItem key={q.slug} value={q.slug}>
                      {q.label || q.slug}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        )}

        <div
          className="relative mx-auto w-full overflow-hidden rounded-lg border bg-muted/30"
          style={{ aspectRatio: `${dims.w} / ${dims.h}`, maxWidth: format === 'story' ? 300 : 420 }}
        >
          {preview && <img src={preview} alt="Preview" className="h-full w-full object-contain" />}
          {(rendering || photoLoading) && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/40">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
        <p className="text-center text-xs text-muted-foreground">
          {KIT_FORMATS.find((f) => f.id === format)?.size}
          {photo && format !== 'quote' && !photoDataUrl && !photoLoading ? ' · this photo could not be loaded, so the design uses colour' : ''}
        </p>

        <div className="flex flex-wrap justify-center gap-2">
          <Button onClick={download}>
            <Download className="mr-2 h-4 w-4" />
            Download PNG
          </Button>
          {canShareFiles && format !== 'poster' && (
            <Button variant="outline" onClick={share}>
              <Share2 className="mr-2 h-4 w-4" />
              Share
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
